import { chain, head, includes, some, values } from 'lodash'

import { AosAirport, MainAirport } from '../../flightInformation/types/AosAirport'
import { TaskAccessLevel } from '../../users/types/AosUser'
import { AosUserLocalisation } from '../../users/types/AosUserLocalisation'
import {
    canEditTasksAt,
    canViewFodAt,
    canViewTasksAt,
    DualReadGates,
    grantsGate,
    isRbacEnabled,
} from './permissions'
import { Role, UnitRoles } from './Role'

/**
 * @deprecated Legacy auth identity wrapper. The canonical surface is now
 * the `aos-auth` package — use the hooks instead:
 *
 * - `useUser()` for `{ id, name, displayName, ... }`
 * - `useHasRole(role)` / `useHasAnyRole([...])` for role checks
 * - `useRoles()` for the full role list
 * - `usePreferences(userId)` for `siteLocation` / `language`
 *
 * `CurrentUser` is only still constructed because ~60 connected
 * components and feature sagas still call `currentUser.isApoc()` /
 * `.canViewTasks()` / etc. via `currentUserSelector`. After AOS-596 MR 10
 * (Phase 3) it is built from the `state.session` slice via
 * `CurrentUser.fromSession(...)` rather than from a Keycloak JWT — there
 * is no JWT decode path in Redux any more.
 *
 * **Do not construct or import `CurrentUser` in new code.**
 */
export class CurrentUser {
    constructor(
        private readonly _roles: Role[] = [],
        private readonly _siteLocation: AosAirport = MainAirport,
        private readonly _localisation: AosUserLocalisation = AosUserLocalisation.en_US,
        private readonly _airports: ReadonlyArray<string> = [],
    ) {}

    /**
     * AOS-596 MR 10 Phase 3 — factory used by `currentUserSelector` to
     * rebuild a `CurrentUser` from the plain-object `state.session` slice.
     * Accepts the loose `string[]` shape `aos-auth` exposes and coerces
     * into the typed `Role[]` the class expects internally.
     */
    public static fromSession(
        roles: ReadonlyArray<string>,
        siteLocation: AosAirport = MainAirport,
        localisation: AosUserLocalisation = AosUserLocalisation.en_US,
        airports: ReadonlyArray<string> = [],
    ): CurrentUser {
        return new CurrentUser(roles as Role[], siteLocation, localisation, airports)
    }

    public get userRoles(): Role[] {
        return [...this._roles]
    }

    public userHasRole = (r: Role): boolean => includes(this._roles, r)

    public userHasAnyOfTheseRoles = (roles: Role[]): boolean =>
        some(roles, r => this.userHasRole(r))

    public userHasAnyUnitRole = (): boolean => this.userHasAnyOfTheseRoles(UnitRoles)

    //TODO: isApoc should be removed when Finavia will migrate to new roles
    public isApoc = () => this.unitRole === Role.ApocUser

    public isEventAdmin = () => grantsGate(this._roles, DualReadGates.eventAdmin)

    public isNonApocTaskAdmin = () => grantsGate(this._roles, DualReadGates.taskAdmin)

    public isAosAdmin = () => grantsGate(this._roles, DualReadGates.aosAdmin)

    public isUserAdmin = () => grantsGate(this._roles, DualReadGates.userAdmin)

    public isNonApocFodAdmin = () => grantsGate(this._roles, DualReadGates.fodAdmin)

    public isNetwork = () => this.siteLocation !== MainAirport

    public isMainAirport = () => this.siteLocation === MainAirport

    public get siteLocation(): AosAirport {
        return this._siteLocation
    }

    public get localisation(): AosUserLocalisation {
        return this._localisation
    }

    private get taskPermissions(): Role[] | null {
        return this._roles.length > 0 ? this._roles : null
    }

    public get taskPermission() {
        const viewPermission = this.taskPermissions?.find(
            role => role === `${this.siteLocation}_${TaskAccessLevel.View}`,
        )
        const editPermission = this.taskPermissions?.find(
            role => role === `${this.siteLocation}_${TaskAccessLevel.Edit}`,
        )
        return editPermission || viewPermission || TaskAccessLevel.None
    }

    /** AOS-1165 — mirror of the backend VIEW-level task gate, see {@link canViewTasksAt}. */
    public canViewTasks() {
        return canViewTasksAt(this._roles, this._airports, this.siteLocation)
    }

    /** AOS-1165 — mirror of the backend EDIT-level task gate, see {@link canEditTasksAt}. */
    public canEditTasks() {
        return canEditTasksAt(this._roles, this._airports, this.siteLocation)
    }

    public canEditDiscounts() {
        return grantsGate(this._roles, DualReadGates.discountEditor)
    }

    /** AOS-1269 — FOD list visibility, see {@link canViewFodAt}. */
    public canViewFod = () => canViewFodAt(this._roles, this._airports, this.siteLocation)

    /** FOD create/edit: `aos:fod:write|admin`, or task edit rights while dual-read. */
    public canEditFod = () =>
        grantsGate(this._roles, DualReadGates.fodEditor) ||
        (!isRbacEnabled() && this.canEditTasks())

    get unitRole(): Role | undefined {
        return head(
            chain(values(UnitRoles))
                .filter(unit => includes(this._roles, unit))
                .value(),
        )
    }
}
